import { HttpClient } from '@angular/common/http';
import {
  Component,
  ElementRef,
  inject,
  type OnDestroy,
  type OnInit,
  ViewChild,
} from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Subscription, take } from 'rxjs';
import type { ProductSubscription } from '../../../server/models/product-subscription.model';
import * as BillingGet from '../../../server/routes/billing/get';
import * as Invite from '../../../server/routes/users/post';
import {
  generatePath,
  nameMaxLength,
} from '../../../server/services/utils.service';
import type { Org, OrgSpace } from '../../../server/types/org-types';
import type { DeepReadonly } from '../../../server/util-type';
import { AuthUserService } from '../auth-user.service';
import { TitleService } from '../title.service';
import { UtilsService } from '../utils.service';

@Component({
  selector: 'app-user-add',
  templateUrl: './user-add.component.html',
  styleUrls: ['./user-add.component.scss'],
  standalone: false,
})
export class UserAddComponent implements OnInit, OnDestroy {
  private http = inject(HttpClient);
  private authUserService = inject(AuthUserService);
  private utils = inject(UtilsService);
  private router = inject(Router);
  private titleService = inject(TitleService);

  accountId: string;
  org: DeepReadonly<Org>;
  orgId: string;

  @ViewChild('nameElement') nameElement: ElementRef;

  form2: FormGroup<{
    users: FormArray<
      FormGroup<{
        name: FormControl<string>;
        email: FormControl<string>;
        spaces: FormArray<FormControl<boolean>>;
      }>
    >;
  }>;

  sub:
    | {
        productSubscription?: ProductSubscription;
        creditCard?: { brand: string; last4: string };
        isActive: boolean;
      }
    | undefined;

  spaceArray: DeepReadonly<OrgSpace[]>;
  waiting = false;
  errorState?: 'unknownError';
  disableSubmit = true;

  addedUsers: string[] = [];

  subscriptions = new Subscription();

  ngOnInit(): void {
    this.titleService.set('Add users');

    const sub = this.authUserService.userOrgObj.pipe(take(1)).subscribe((u) => {
      if (!u) throw new Error('userOrgObj not set when it was expected.');
      // First generate the form group

      this.spaceArray = u.org.spaces;

      this.accountId = u.accountId;
      this.org = u.org;
      this.orgId = u.org._id;

      this.form2 = new FormGroup({
        users: new FormArray([this.userLine()]),
      });

      const formSub = this.form2.valueChanges.subscribe(() =>
        this.getUsersAndCheckErrors()
      );
      this.subscriptions.add(formSub);

      const path = generatePath<BillingGet.ReqParams>(
        '/api/orgs/:orgId/billing',
        { orgId: this.orgId }
      );
      this.http.get<BillingGet.ResBody>(path).subscribe({
        next: ({ productSubscription, creditCard }) => {
          const isActive =
            !!productSubscription?.endAt &&
            productSubscription?.endAt > Date.now();
          this.sub = { productSubscription, creditCard, isActive };
        },
      });
    });
    this.subscriptions.add(sub);
  }

  addUser() {
    this.form2.controls.users.push(this.userLine());
  }

  userLine() {
    return new FormGroup({
      name: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, Validators.maxLength(nameMaxLength)],
      }),
      email: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, Validators.email],
      }),
      spaces: new FormArray(
        this.spaceArray.map(
          (space) => new FormControl(false, { nonNullable: true })
        ),
        Validators.required
      ),
    });
  }

  removeUser(index: number) {
    this.form2.controls.users.removeAt(index);
  }

  submitUsersToServer() {
    const users = this.getUsersAndCheckErrors(true);

    if (!users || users.length === 0) return;

    const org = this.org;
    let personCount = org.users.length;
    const body: Invite.ReqBody = users.map((u) => {
      // As we go around the color wheel, this gives the most unique colors for
      // 4 people. Then as we loop around the color wheel the next set of 4 is
      // also distinct from the previous set of 4.
      personCount++;
      const color = (240 + personCount * 99) % 360;
      return { ...u, color };
    });

    const { orgId } = this;
    const path = generatePath<Invite.ReqParams>('/api/orgs/:orgId/users', {
      orgId,
    });
    this.waiting = true;
    this.disableSubmit = true;
    this.form2.disable();

    const obs = this.http.post<Invite.ResBody>(path, body).subscribe({
      next: ({ org: o, urls }) => {
        this.waiting = false;
        this.authUserService.updateOrg({ org: o });
        if (urls) console.log(urls); // Do not delete, for debug
        this.router.navigate(['/org', this.orgId, 'inbox']);
      },
      error: (error) => {
        this.waiting = false;
        this.disableSubmit = false;
        this.form2.enable();
        if (error.status === 0) {
          this.utils.launchOfflineNotification();
        } else {
          this.errorState = 'unknownError';
        }
      },
    });
    return obs;
  }

  getUsersAndCheckErrors(flagErrors?: boolean) {
    const usersRaw = this.form2.controls.users.getRawValue();

    const preSubmitUsers = usersRaw.map((u) => {
      const user: { name: string; email: string; spaceIds: string[] } = {
        name: u.name,
        email: u.email,
        spaceIds: [],
      };

      u.spaces.forEach((val, index) => {
        if (val) user.spaceIds.push(this.spaceArray[index]._id);
      });

      return user;
    });

    // Trim
    preSubmitUsers.forEach((u) => {
      u.name = u.name.trim();
      u.email = u.email.trim().toLowerCase();
    });

    // Set errors
    let formError = false;
    preSubmitUsers.forEach((u, index) => {
      const controls = this.form2.controls.users.controls[index].controls;
      if (!u.name) {
        if (flagErrors) {
          controls.name.setErrors({ required: true });
          controls.name.markAsTouched();
        }
        formError = true;
      }
      if (!u.email) {
        if (flagErrors) {
          controls.email.setErrors({ required: true });
          controls.email.markAsTouched();
        }
        formError = true;
      }
      if (u.spaceIds.length === 0) {
        if (this.spaceArray.length === 1) {
          u.spaceIds.push(this.spaceArray[0]._id);
        } else {
          if (flagErrors) {
            controls.spaces.setErrors({});
            controls.spaces.markAsTouched();
          }
          formError = true;
        }
      }
    });
    if (formError || preSubmitUsers.length === 0) {
      this.disableSubmit = true;
      return;
    }

    this.disableSubmit = false;

    return preSubmitUsers;
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
